import { useEffect, useRef, useState } from "react";
import { NavigationType, useLocation, useNavigationType } from "react-router-dom";
import { FilterValues, IFilterSection } from "@models/IFilterConfig";
import { areFiltersEqual, filtersFromApiParams } from "./filterBarUtils";

/** Filter values encoded in a URL query string; params listed in `nonFilterParams` are ignored. */
export function filtersFromSearch(search: string, sections: IFilterSection[], nonFilterParams: string[]): FilterValues | undefined {
    const params: { [key: string]: string } = {};

    new URLSearchParams(search).forEach((value, key) => {
        if (!nonFilterParams.includes(key)) {
            params[key] = value;
        }
    });

    const parsed = filtersFromApiParams(params, sections);

    return Object.keys(parsed).length ? parsed : undefined;
}

/**
 * Deep-link filters for a FilterBar screen, kept in step with browser history.
 *
 * The URL is read on mount, and again when a POP navigation (Back / Forward) lands on a query string
 * whose filters differ from the ones last seen. Render the FilterBar with `key={historyKey}` so that
 * change remounts it with the new `initialFilters` and re-runs the mount search. The adapter's own
 * `navigate()` calls (PUSH / REPLACE) and pagination-only URL changes never remount.
 *
 * `isFirstSearch` tells the adapter to `replace` rather than `push` the URL: true on mount and again
 * after a history-driven remount, so the re-run search does not add a duplicate history entry.
 */
export function useUrlFilters(sections: IFilterSection[], nonFilterParams: string[]) {
    const location = useLocation();
    const navigationType = useNavigationType();

    const [initialFilters, setInitialFilters] = useState<FilterValues | undefined>(() =>
        filtersFromSearch(location.search, sections, nonFilterParams)
    );
    const [historyKey, setHistoryKey] = useState(0);
    const isFirstSearch = useRef(true);

    const lastSearch = useRef(location.search);
    const lastFilters = useRef(initialFilters);

    useEffect(() => {
        if (location.search === lastSearch.current) {
            return;
        }

        lastSearch.current = location.search;

        const next = filtersFromSearch(location.search, sections, nonFilterParams);
        const changed = !areFiltersEqual(next, lastFilters.current, sections);

        lastFilters.current = next;

        if (navigationType !== NavigationType.Pop || !changed) {
            return;
        }

        setInitialFilters(next);
        isFirstSearch.current = true;
        setHistoryKey((key) => key + 1);
    }, [location.search, navigationType, sections, nonFilterParams]);

    return { initialFilters, historyKey, isFirstSearch };
}
